import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Input } from './ui/input';
import { StandardButton } from '../styles/StandardButtons';
import { AuthBanner } from './AuthBanner';
import { supabase } from '../lib/supabase';
import { waitForInitialSession } from '../lib/auth-session';
import {
  acceptInvitation, previewInvitation, rememberPendingInvitation, forgetPendingInvitation,
  type AcceptResult, type InvitationPreview,
} from '../lib/commercial-invitation-token';
import './Setup.css';

const MIN_PASSWORD_LENGTH = 8;

const ACCEPT_MESSAGES: Record<Exclude<AcceptResult['status'], 'accepted' | 'already_accepted'>, string> = {
  not_found: 'This invitation link is not valid anymore.',
  expired: 'This invitation expired. Ask the company to send you a new one.',
  revoked: 'This invitation was cancelled by the company.',
  email_not_confirmed: 'Confirm your email address first (check your inbox), then open this link again.',
  email_mismatch: 'You are signed in with a different email than the one invited. Sign out and use the invited address.',
};

/**
 * Where the link of a commercial invitation lands (/auth/join?token=...). Public: it works without a session. It shows who invited the person and as what, lets them
 * create the free account (the email is fixed to the invited one) or, if they already have one, sign in, and then accepts the invitation. Accepting only gives the
 * account its type: no company, project or permission comes with it -- the company grants those separately.
 */
export function JoinPage() {
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [signedInEmail, setSignedInEmail] = useState<string | null>(null);
  const [phase, setPhase] = useState<'loading' | 'invalid' | 'form' | 'confirm-email' | 'accepted'>('loading');
  const [message, setMessage] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) { setPhase('invalid'); setMessage(ACCEPT_MESSAGES.not_found); return; }
      try {
        const [shown, session] = await Promise.all([previewInvitation(token), waitForInitialSession()]);
        if (cancelled) return;
        if (shown.status !== 'pending') {
          setPhase('invalid');
          setMessage(shown.status === 'expired' ? ACCEPT_MESSAGES.expired : shown.status === 'revoked' ? ACCEPT_MESSAGES.revoked : ACCEPT_MESSAGES.not_found);
          if (shown.status === 'not_found') forgetPendingInvitation();
          return;
        }
        setPreview(shown);
        setSignedInEmail(session?.user?.email?.toLowerCase() ?? null);
        setPhase('form');
      } catch (e) {
        if (!cancelled) { setPhase('invalid'); setMessage(e instanceof Error ? e.message : 'The invitation could not be checked.'); }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const accept = async () => {
    setBusy(true); setError(null);
    try {
      const result = await acceptInvitation(token);
      if (result.status === 'accepted' || result.status === 'already_accepted') {
        forgetPendingInvitation();
        setPhase('accepted');
      } else {
        if (result.status === 'not_found' || result.status === 'expired' || result.status === 'revoked') forgetPendingInvitation();
        setError(ACCEPT_MESSAGES[result.status]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The invitation could not be accepted.');
    } finally {
      setBusy(false);
    }
  };

  const createAccount = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy || !preview?.email) return;
    setError(null);
    if (password.length < MIN_PASSWORD_LENGTH) { setError(`Your password must have at least ${MIN_PASSWORD_LENGTH} characters.`); return; }
    setBusy(true);
    try {
      const { data, error: signUpError } = await supabase.auth.signUp({ email: preview.email, password });
      if (signUpError) {
        const alreadyRegistered = /already (registered|exists)/i.test(signUpError.message || '');
        setError(alreadyRegistered ? 'An account with this email already exists. Sign in to accept the invitation.' : signUpError.message || 'Sign up failed.');
        return;
      }
      // Auth answers an existing address with no new identity (it never confirms whether the address is registered)
      if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
        setError('An account with this email already exists. Sign in to accept the invitation.');
        return;
      }
      rememberPendingInvitation(token);
      setPhase('confirm-email');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sign up failed.');
    } finally {
      setBusy(false);
    }
  };

  const signedInAsInvited = !!signedInEmail && !!preview?.email && signedInEmail === preview.email.toLowerCase();
  const role = preview?.kind === 'commercial_vendor' ? 'vendor' : 'client';

  return (
    <div className="setup-container">
      <div className="setup-content" style={{ maxWidth: '28rem' }}>
        <AuthBanner />
        {phase === 'loading' && <p>Checking your invitation…</p>}

        {phase === 'invalid' && (
          <>
            <h1 className="h1">Invitation unavailable</h1>
            <p role="alert" className="mt-2 text-sm text-gray-700">{message}</p>
            <p className="mt-4 text-sm"><Link to="/login" className="text-blue-600 hover:underline">Go to login</Link></p>
          </>
        )}

        {phase === 'form' && preview && (
          <>
            <h1 className="h1">{preview.company_name ? `${preview.company_name} invited you` : 'You are invited'}</h1>
            <p className="mt-2 text-sm text-gray-700">
              Create your free KrewUp account as a {role}. Having an account does not give you access to anything by itself: the company chooses what you can see.
            </p>
            {error && <div className="error-message mt-3" role="alert">{error}</div>}

            {signedInEmail && !signedInAsInvited && (
              <div className="mt-4 text-sm">
                <p>You are signed in as {signedInEmail}, but this invitation is for {preview.email}.</p>
                <div className="mt-3 flex justify-end">
                  <StandardButton customVariant="white" onClick={() => { rememberPendingInvitation(token); supabase.auth.signOut().finally(() => window.location.reload()); }}>Sign out</StandardButton>
                </div>
              </div>
            )}

            {signedInAsInvited && (
              <div className="mt-4 flex justify-end">
                <StandardButton customVariant="black" disabled={busy} onClick={accept}>{busy ? 'Accepting…' : 'Accept invitation'}</StandardButton>
              </div>
            )}

            {!signedInEmail && (
              <form onSubmit={createAccount} className="mt-4">
                <div className="form-group">
                  <label htmlFor="join-email" className="form-label">Email</label>
                  <Input id="join-email" type="email" value={preview.email || ''} readOnly disabled />
                </div>
                <div className="form-group">
                  <label htmlFor="join-password" className="form-label">Choose a password</label>
                  <Input id="join-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={MIN_PASSWORD_LENGTH} autoComplete="new-password" required />
                  <small className="text-muted">Minimum {MIN_PASSWORD_LENGTH} characters</small>
                </div>
                <div className="mt-4 flex justify-end">
                  <StandardButton customVariant="black" type="submit" disabled={busy}>{busy ? 'Creating…' : 'Create free account'}</StandardButton>
                </div>
                <p className="mt-4 text-sm text-gray-600">
                  Already have an account?{' '}
                  <Link to="/login" onClick={() => rememberPendingInvitation(token)} className="text-blue-600 hover:underline">Sign in</Link>
                </p>
              </form>
            )}
          </>
        )}

        {phase === 'confirm-email' && (
          <>
            <h1 className="h1">Check your email</h1>
            <p className="mt-2 text-sm text-gray-700" data-testid="join-confirm-email">
              We sent a confirmation link to {preview?.email}. After you confirm it, sign in and your invitation is accepted automatically.
            </p>
          </>
        )}

        {phase === 'accepted' && (
          <>
            <h1 className="h1">Your free account is ready</h1>
            <p className="mt-2 text-sm text-gray-700" data-testid="join-accepted">
              The invitation was accepted. You do not have access to any project yet: the company will add you, and it will appear here when they do.
            </p>
            <div className="mt-4 flex justify-end">
              <StandardButton customVariant="black" onClick={() => { window.location.href = '/'; }}>Continue</StandardButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
